/**
 * Offline-Aware API Client
 *
 * Wraps the base apiClient with offline caching capabilities:
 * - Caches GET responses in localStorage with TTL
 * - Returns cached data when offline
 * - Queues POST/PUT/DELETE for later sync
 * - Suppresses error spam when offline
 */

import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { apiClient } from './apiClient';

// Cache configuration
const CACHE_PREFIX = 'weavein_api_cache_';
const DEFAULT_TTL_MS = 60 * 60 * 1000; // 1 hour default
const MAX_CACHE_ENTRIES = 100;

// Track online status
let isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
let lastOfflineLogTime = 0;
const OFFLINE_LOG_THROTTLE_MS = 10000; // Only log offline errors every 10 seconds

// Listen for online/offline events
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    isOnline = true;
    console.log('[OfflineAPI] Back online');
  });
  window.addEventListener('offline', () => {
    isOnline = false;
    console.log('[OfflineAPI] Gone offline');
  });
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
  url: string;
}

interface CacheOptions {
  /** Time to live in milliseconds */
  ttl?: number;
  /** Skip cache and always fetch fresh */
  skipCache?: boolean;
  /** Cache key override (defaults to URL) */
  cacheKey?: string;
}

/**
 * Get cached data if valid
 */
function getFromCache<T>(key: string): T | null {
  try {
    const cacheKey = CACHE_PREFIX + key;
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    const entry: CacheEntry<T> = JSON.parse(cached);
    const age = Date.now() - entry.timestamp;

    // Return cached data even if expired when offline
    if (age < entry.ttl || !isOnline) {
      return entry.data;
    }

    // Cache expired and we're online, remove it
    localStorage.removeItem(cacheKey);
    return null;
  } catch {
    return null;
  }
}

/**
 * Save data to cache.
 * Skips caching member data when a reveal token is active to prevent
 * storing unmasked PII in localStorage.
 */
function saveToCache<T>(key: string, data: T, ttl: number, url: string): void {
  try {
    // Do not cache member data when reveal session is active (data contains unmasked PII)
    const hasRevealToken = !!sessionStorage.getItem('reveal_token');
    if (hasRevealToken && url.includes('/members')) {
      return;
    }

    const cacheKey = CACHE_PREFIX + key;
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl,
      url,
    };
    localStorage.setItem(cacheKey, JSON.stringify(entry));

    // Cleanup old entries if we have too many
    cleanupOldCacheEntries();
  } catch (error) {
    // Storage might be full, try to cleanup
    cleanupOldCacheEntries();
  }
}

/**
 * Remove oldest cache entries if we exceed max
 */
function cleanupOldCacheEntries(): void {
  try {
    const entries: { key: string; timestamp: number }[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(CACHE_PREFIX)) {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const entry = JSON.parse(cached);
            entries.push({ key, timestamp: entry.timestamp || 0 });
          }
        } catch {
          // Invalid entry, remove it
          if (key) localStorage.removeItem(key);
        }
      }
    }

    // If we have too many, remove oldest
    if (entries.length > MAX_CACHE_ENTRIES) {
      entries.sort((a, b) => a.timestamp - b.timestamp);
      const toRemove = entries.slice(0, entries.length - MAX_CACHE_ENTRIES);
      toRemove.forEach(({ key }) => localStorage.removeItem(key));
    }
  } catch {
    // Ignore cleanup errors
  }
}

/**
 * Generate cache key from URL and params
 */
function generateCacheKey(url: string, params?: Record<string, any>): string {
  let key = url;
  if (params && Object.keys(params).length > 0) {
    const sortedParams = Object.keys(params)
      .sort()
      .map((k) => `${k}=${params[k]}`)
      .join('&');
    key += `?${sortedParams}`;
  }
  return key.replace(/[^a-zA-Z0-9_-]/g, '_');
}

/**
 * Log offline error with throttling to prevent spam
 */
function logOfflineError(url: string): void {
  const now = Date.now();
  if (now - lastOfflineLogTime > OFFLINE_LOG_THROTTLE_MS) {
    console.warn(`[OfflineAPI] Offline - request to ${url} failed. Using cached data if available.`);
    lastOfflineLogTime = now;
  }
}

/**
 * Offline-aware GET request
 * Returns cached data when offline or on network error
 */
export async function offlineGet<T = any>(
  url: string,
  config?: AxiosRequestConfig & { cacheOptions?: CacheOptions }
): Promise<AxiosResponse<T>> {
  const { cacheOptions, ...axiosConfig } = config || {};
  const ttl = cacheOptions?.ttl ?? DEFAULT_TTL_MS;
  const cacheKey = cacheOptions?.cacheKey ?? generateCacheKey(url, axiosConfig.params);

  // If explicitly skipping cache and online, just fetch
  if (cacheOptions?.skipCache && isOnline) {
    const response = await apiClient.get<T>(url, axiosConfig);
    saveToCache(cacheKey, response.data, ttl, url);
    return response;
  }

  // Try cache first if offline
  if (!isOnline) {
    const cached = getFromCache<T>(cacheKey);
    if (cached !== null) {
      return {
        data: cached,
        status: 200,
        statusText: 'OK (cached)',
        headers: {},
        config: axiosConfig as any,
      } as AxiosResponse<T>;
    }
    logOfflineError(url);
    throw new Error('Offline and no cached data available');
  }

  // Online - try to fetch, fall back to cache on error
  try {
    const response = await apiClient.get<T>(url, axiosConfig);
    saveToCache(cacheKey, response.data, ttl, url);
    return response;
  } catch (error: any) {
    // Network error - try cache
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      const cached = getFromCache<T>(cacheKey);
      if (cached !== null) {
        logOfflineError(url);
        return {
          data: cached,
          status: 200,
          statusText: 'OK (cached)',
          headers: {},
          config: axiosConfig as any,
        } as AxiosResponse<T>;
      }
    }
    throw error;
  }
}

/**
 * Check if we're currently online
 */
export function isCurrentlyOnline(): boolean {
  return isOnline;
}

/**
 * Get all cached keys (for debugging)
 */
export function getCachedKeys(): string[] {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(CACHE_PREFIX)) {
      keys.push(key.replace(CACHE_PREFIX, ''));
    }
  }
  return keys;
}

/**
 * Clear all API cache
 */
export function clearApiCache(): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(CACHE_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
}

/**
 * Pre-cache critical data for offline use
 * Call this when user logs in or periodically while online
 */
export async function preCacheForOffline(orgId: number): Promise<void> {
  if (!isOnline) return;

  const criticalEndpoints = [
    `/api/members/?page=1&page_size=100`,
    `/api/programs?cbo_id=${orgId}`,
    `/api/enrollments/?page=1&page_size=100`,
    `/api/cbos`,
  ];

  console.log('[OfflineAPI] Pre-caching critical data for offline use...');

  const results = await Promise.allSettled(
    criticalEndpoints.map((url) =>
      offlineGet(url, {
        headers: { 'X-CBO-ID': orgId.toString() },
        cacheOptions: { ttl: 4 * 60 * 60 * 1000 }, // 4 hours for pre-cached data
      })
    )
  );

  const successful = results.filter((r) => r.status === 'fulfilled').length;
  console.log(`[OfflineAPI] Pre-cached ${successful}/${criticalEndpoints.length} endpoints`);
}

/**
 * Clear only member-related cache entries (used by DataShieldContext on reveal revocation)
 */
export function clearMemberCache(): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(CACHE_PREFIX) && key.includes('members')) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
}

export default {
  get: offlineGet,
  isOnline: isCurrentlyOnline,
  clearCache: clearApiCache,
  clearMemberCache,
  preCacheForOffline,
};
